import { setArticleFolderDetailsData } from '../../../../redux/articleFolderDetailsDataSlice';
import { SuccessfullAddArticleItemDialog } from './successfullAddArticleItemDialog';
import { CustomErrorLogger } from '../../../../utils/CustomErrorLogger';
import { useHasPermission } from '../../../../hooks/useHasPermission';
import { EditArticleItemDialog } from './editArticleItemDialog';
import { AddArticleItemDialog } from './addArticleItemDialog';
import useResponsive from '../../../../hooks/useResponsive';
import { useSelector, useDispatch } from 'react-redux';
import { useArticleMediaViewer } from '../../useArticleMediaViewer';
import { DeleteRecord } from '../../../deleteRecord';
import { Box, Typography, Button, ButtonGroup } from '@mui/material';
import NoRecord from '../../../NoRecord';
import React, { useMemo, useState } from 'react';
import Iconify from '../../../Iconify';
import axios from 'axios';
import '../../../../css/machines/MachineFiltration.css';
import {
  DEFAULT_STATUS_FILTER,
  PUBLICATION_STATUS_LABELS,
} from '../../../../utils/articleItemMediaUtils';
import { ArticlePublicationStatusChip } from '../../ArticleMetaChips';
import ContentMediaGallery from '../../../contentMedia/ContentMediaGallery';
import { getContentGalleryItems } from '../../../../utils/contentMediaUtils';
import RichTextDisplay from '../../../richText/RichTextDisplay';
import OrderMoveButtons from '../../../listOrder/OrderMoveButtons';
import { swapListItem } from '../../../../utils/listOrder';

const ALL_PUBLICATION_STATUSES = Object.keys(PUBLICATION_STATUS_LABELS);

const isAllStatusesSelected = (filter) =>
  ALL_PUBLICATION_STATUSES.every((status) => filter.includes(status));

const ArticleItems = () => {

    const dispatch = useDispatch();
    const isDesktop = useResponsive('up', 'lg');
    const hasArticleItemViewPermission = useHasPermission('article_view');
    const hasArticleItemCreatePermission = useHasPermission('article_create');
    const hasArticleItemUpdatePermission = useHasPermission('article_update');
    const hasArticleItemDeletePermission = useHasPermission('article_delete');
    const hasDossierViewPermission = useHasPermission('dossier_view');
    const canViewArticlesSection = hasArticleItemViewPermission || hasDossierViewPermission;
    const articleFolderDetailsData = useSelector((state) => state.ArticleFolderDetailsData);
    const { openArticleMedia, mediaViewer } = useArticleMediaViewer();

    const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS_FILTER);
    const [openAddArticleItem, setOpenAddArticleItem] = useState(false);
    const [articleToDelete, setArticleToDelete] = useState(null);
    const [openDeleteArticleItem, setOpenDeleteArticleItem] = useState(false);
    const [editArticleItem, setEditArticleItem] = useState({ data: null, open: false });
    const [openSuccessfulAddArticleItem, setOpenSuccessfulAddArticleItem] = useState(false);

    const filteredArticles = useMemo(() => (
        (articleFolderDetailsData.article || []).filter((item) =>
            statusFilter.includes(item.publication_status)
        )
    ), [articleFolderDetailsData.article, statusFilter]);

    const openDeleteArticleDialog = (id) => {
        setArticleToDelete(id);
        setOpenDeleteArticleItem(true);
    };

    const moveArticle = async (id, direction) => {
        const articles = articleFolderDetailsData.article || [];
        try {
            const response = await axios.patch(`article/${id}/move`, { direction });
            if (response.data?.moved) {
                dispatch(setArticleFolderDetailsData({
                    ...articleFolderDetailsData,
                    article: swapListItem(articles, id, direction),
                }));
            }
        } catch (error) {
            CustomErrorLogger(error, 'Move article');
        }
    };

    const handleDeleteArticle = async () => {
        try {
            const response = await axios.delete(`article/${articleToDelete}`);
            if (response.status === 200 || response.status === 201) {
                setOpenDeleteArticleItem(false);
                dispatch(setArticleFolderDetailsData({
                    ...articleFolderDetailsData,
                    article: articleFolderDetailsData.article.filter((item) => item.id !== articleToDelete),
                }));
            }
        } catch (error) {
            console.error('Error deleting article:', error);
            CustomErrorLogger(error, "Delete article");
        }
    };

    const handleSelectAllStatuses = () => {
        setStatusFilter((prev) => (
            isAllStatusesSelected(prev) ? [...DEFAULT_STATUS_FILTER] : [...ALL_PUBLICATION_STATUSES]
        ));
    };

    const handleToggleStatus = (status) => {
        setStatusFilter((prev) => {
            if (prev.includes(status)) {
                const next = prev.filter((value) => value !== status);
                return next.length > 0 ? next : prev;
            }
            return [...prev, status];
        });
    };

    const getStatusButtonClass = (status) => (
        statusFilter.includes(status)
            ? 'MachineFiltration_options_selected_button'
            : 'MachineFiltration_options_button'
    );

    return (
        canViewArticlesSection ? (
        <Box sx={{ backgroundColor: '#FFFFFF', margin: '30px 0px 10px 0px', borderRadius: '8px', padding: '20px' }}>
            <Box className='modeEtConsigne_edit_box' sx={{ marginBottom: '20px' }}>
                <Typography className='modeEtConsigne_title'>Articles</Typography>
                {isDesktop && hasArticleItemCreatePermission && articleFolderDetailsData.article.length > 0 &&
                    <Box className='role_page_header_action_box' onClick={() => setOpenAddArticleItem(true)}>
                        <Iconify className='role_page_header_add_icon' icon={"ic:baseline-plus"} />
                        <Typography className='role_page_header_action_title'>Ajouter un article</Typography>
                    </Box>
                }
                {!isDesktop &&
                    <Box className='role_page_header_action_box'>
                        {hasArticleItemCreatePermission && <Iconify className='role_page_header_add_icon' icon={"ic:baseline-plus"} onClick={() => setOpenAddArticleItem(true)} />}
                    </Box>
                }
            </Box>

            {articleFolderDetailsData.article.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <ButtonGroup variant="outlined">
                        <Button
                            className={isAllStatusesSelected(statusFilter)
                                ? 'MachineFiltration_options_selected_button'
                                : 'MachineFiltration_options_button'}
                            onClick={handleSelectAllStatuses}
                        >
                            Tout
                        </Button>
                        {Object.entries(PUBLICATION_STATUS_LABELS).map(([value, label]) => (
                            <Button
                                key={value}
                                className={getStatusButtonClass(value)}
                                onClick={() => handleToggleStatus(value)}
                            >
                                {label}
                            </Button>
                        ))}
                    </ButtonGroup>
                </Box>
            )}

            {articleFolderDetailsData.article.length === 0 ?
                <NoRecord
                    description="Aucun article ajouté pour le moment"
                    buttonTitle="Ajouter un article"
                    handleOpenDialog={() => setOpenAddArticleItem(true)}
                    hasPermission={hasArticleItemCreatePermission}
                />
                : filteredArticles.length === 0 ?
                <NoRecord description="Aucun article pour les statuts sélectionnés" />
                :
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {filteredArticles.map((item) => {
                        const fullIndex = (articleFolderDetailsData.article || []).findIndex((row) => row.id === item.id);
                        const articleCount = (articleFolderDetailsData.article || []).length;
                        return (
                        <Box
                            key={item.id}
                            sx={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%', backgroundColor: '#F6F6F6', borderRadius: '8px', padding: '10px' }}
                        >
                            <Box className='modeEtConsigne_edit_box'>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography className='modeEtConsigne_title'>{item.title}</Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                        <ArticlePublicationStatusChip status={item.publication_status} />
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {hasArticleItemUpdatePermission && (
                                        <OrderMoveButtons
                                            disableUp={fullIndex <= 0}
                                            disableDown={fullIndex < 0 || fullIndex >= articleCount - 1}
                                            onMove={(direction) => moveArticle(item.id, direction)}
                                        />
                                    )}
                                    {hasArticleItemDeletePermission && <Iconify className='modeEtConsigne_edit_icon' sx={{ color: '#F12525 !important' }} icon={"ic:outline-delete"} onClick={() => openDeleteArticleDialog(item.id)} />}
                                    {hasArticleItemUpdatePermission && <Iconify className='modeEtConsigne_edit_icon' icon={"ic:outline-edit"} onClick={() => setEditArticleItem({ data: item, open: true })} />}
                                </Box>
                            </Box>
                            <RichTextDisplay html={item.description} />
                            {hasArticleItemViewPermission && getContentGalleryItems(item).length > 0 && (
                                <ContentMediaGallery
                                    readOnly
                                    items={getContentGalleryItems(item)}
                                    onOpen={(index) => openArticleMedia(item, index)}
                                />
                            )}
                        </Box>
                    );
                    })}
                </Box>
            }

            {openAddArticleItem &&
                <AddArticleItemDialog
                    openAddArticleItem={openAddArticleItem}
                    setOpenAddArticleItem={setOpenAddArticleItem}
                    setOpenSuccessfulAddArticleItem={setOpenSuccessfulAddArticleItem}
                />
            }

            {openSuccessfulAddArticleItem &&
                <SuccessfullAddArticleItemDialog
                    openSuccessfulAddArticleItem={openSuccessfulAddArticleItem}
                    setOpenSuccessfulAddArticleItem={setOpenSuccessfulAddArticleItem}
                    setOpenAddArticleItem={setOpenAddArticleItem}
                />
            }

            {editArticleItem.open &&
                <EditArticleItemDialog
                    editArticleItem={editArticleItem}
                    setEditArticleItem={setEditArticleItem}
                />
            }

            {openDeleteArticleItem &&
                <DeleteRecord
                    openDelete={openDeleteArticleItem}
                    setOpenDelete={setOpenDeleteArticleItem}
                    handleDelete={handleDeleteArticle}
                    title="Êtes-vous sûr de vouloir supprimer cet article ?"
                />
            }

            {mediaViewer}

        </Box>
        ) : null
    );
};

export default ArticleItems;
